import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../../../components/common/buttons/Button';
import { UserOwnProductDetailCard } from '../../../../views/Product/listView/UserOwnProductDetailCard/UserOwnProductDetailCard';
import { IMyOwnTractor } from '../../../../interfaces/OwnTractor.interfaces';
import { MachineApprovalStatus } from '../../../../constants/machine/machine.constant';
import { userSellTractorSurveySlice } from '../../../../store/userSellTractorSurveySlice';
import { useAppDispatch } from '../../../../hooks/store/useStore';
import { useNavigate } from 'react-router-dom';
import { SlideModal } from '../../../../components/abstract/modal/slide/SlideModal';
import { MoreButtons } from './MoreButtons';
import { useTogglePokeStatus } from '../../../../hooks/user/useTogglePokeStatus';
import { ConfirmDeleteOwnTractorModal } from './ConfirmDeleteOwnTractorModal';
import { useDeleteMyOwnTractorMutation } from '../../../../hooks/api/user/mutations/useDeleteMyOwnTractor.mutation';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { PokeRequestCheckModalBody } from './PokeRequestCheckModalBody';
import { ampli } from '../../../../ampli';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { IMachine } from '../../../../interfaces/Machine.interfaces';
import { pageInfo, sellTractorPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';

export type IPokeActionType = (tractor: IMyOwnTractor) => void;

interface IMyInfoUserOwnProductCardProps {
  userOwnProduct: IMyOwnTractor;
  handlePokeAction: IPokeActionType;
  refetchList: () => void;
}

export const MyInfoUserOwnProductCard = ({
  userOwnProduct,
  handlePokeAction,
  refetchList,
}: IMyInfoUserOwnProductCardProps) => {
  const navigate = useNavigate();
  const [isMoreButtonsModalOpen, setIsMoreButtonsModalOpen] = useState(false);
  const [isPokeRequestCheckModalOpen, setIsPokeRequestCheckModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isDeleteSuccessModalOpen, setIsDeleteSuccessModalOpen] = useState(false);

  const { APPROVAL, PSWAITING, REJECT } = MachineApprovalStatus;

  const { mutate: deleteOwnTractor } = useDeleteMyOwnTractorMutation();
  const { handleTogglePokeStatus } = useTogglePokeStatus({
    tractor: userOwnProduct,
    onSuccess: handlePokeAction,
  });

  // TODO: 로직 리스트 페이지로 옮기기
  const pokingAvailableItem = useMemo(() => {
    const { machine } = userOwnProduct;
    return machine && (machine.approvalStatus === APPROVAL || machine.approvalStatus === PSWAITING);
  }, [userOwnProduct]);

  const showButtonGroup = useMemo(() => {
    return pokingAvailableItem && !userOwnProduct.isSalesPending;
  }, [pokingAvailableItem, userOwnProduct]);

  const showDeleteButton = useMemo(() => {
    const { machine } = userOwnProduct;
    return machine && machine.approvalStatus === REJECT;
  }, [userOwnProduct]);

  const dispatch = useAppDispatch();
  const userSellTractorSurveyActions = userSellTractorSurveySlice.actions;

  const handleSellTractorClick = () => {
    const isEngineOutputInPsAvailable =
      userOwnProduct.machine.approvalStatus === APPROVAL || userOwnProduct.machine.approvalStatus === PSWAITING;
    dispatch(userSellTractorSurveyActions.changeUserOwnTractorId(userOwnProduct.id));
    dispatch(
      userSellTractorSurveyActions.changeSellTractorRequiredInputs({
        machineType: 'tractor',
        brand: userOwnProduct?.brand?.name,
        brandId: userOwnProduct?.brand?.id,
        model: userOwnProduct?.machine?.model,
        engineOutputInPs: isEngineOutputInPsAvailable ? userOwnProduct?.machine.engineOutputInPs : undefined,
        sellTractorMachineInfo: {
          id: userOwnProduct?.machine?.id,
          approvalStatus: userOwnProduct?.machine?.approvalStatus,
          engineOutputInPs: isEngineOutputInPsAvailable ? userOwnProduct?.machine.engineOutputInPs : undefined,
        },
        manufacturedYear: userOwnProduct?.manufacturedYear,
        operationTime: userOwnProduct?.operationTime,
      }),
    );
    navigate(sellTractorPageInfo.overviewPage.path, { replace: true });
  };

  const handlePokeActionFromMore = (tractor: IMyOwnTractor) => {
    setIsMoreButtonsModalOpen(false);
    handlePokeAction(tractor);
  };

  const handleDeleteOwnTractor = () => {
    deleteOwnTractor(
      {
        ownTractorId: userOwnProduct.id,
      },
      {
        onSuccess: (data) => {
          setIsDeleteSuccessModalOpen(true);
        },
      },
    );
  };

  const handlePokeRequestCheck = () => {
    ampli.pvmClickMyOwnListConfirmPokeRequest({
      id: userOwnProduct.id,
      title: getOpenMarketProductTitle({
        brandName: userOwnProduct.brand?.name,
        machine: userOwnProduct.machine as IMachine,
        showPsOnPendingApproval: true,
      }),
    });
    navigate(`${pageInfo.myInfoOwnTractorDetailPage.path}/${userOwnProduct.id}`);
  };

  const handleCloseDeleteSuccessModal = () => {
    setIsDeleteSuccessModalOpen(false);
    refetchList();
  };

  const isApprovedOrWaiting =
    (userOwnProduct.machine.approvalStatus === APPROVAL || userOwnProduct.machine.approvalStatus === PSWAITING) &&
    !userOwnProduct.isSalesPending;

  return (
    <>
      <Wrapper>
        <UserOwnProductDetailCard
          userOwnProduct={userOwnProduct}
          isSalesPending={userOwnProduct.isSalesPending}
          showPsOnPendingApproval
          hasEditImageButton={isApprovedOrWaiting}
          showBottomSection={isApprovedOrWaiting}
        />
        {(showButtonGroup || showDeleteButton) && (
          <ButtonGroup>
            {showButtonGroup && (
              <>
                <Button
                  borderType={'square'}
                  color={'filledPrimaryStrong'}
                  size={'md'}
                  text={{ content: '이 트랙터 판매하기', size: 'md' }}
                  onClick={() => {
                    ampli.pvmClickMySellOwnTractor({
                      page_type: userOwnProduct.isPokeEnabled ? 'PUBLIC' : 'PRIVATE',
                      position: 'list',
                    });
                    handleSellTractorClick();
                  }}
                />
                <BottomButtonGroup>
                  <LeftButtonWrapper>
                    <Button
                      borderType={'square'}
                      color={'filledGreyLight'}
                      size={'md'}
                      text={{
                        content: userOwnProduct.isPokeEnabled ? '가격제안 확인' : '가격제안 받기',
                        size: 'md',
                      }}
                      onClick={userOwnProduct.isPokeEnabled ? handlePokeRequestCheck : handleTogglePokeStatus}
                      hasNotification={userOwnProduct.isPokeEnabled && userOwnProduct.isPokeNotificationActive}
                    />
                  </LeftButtonWrapper>
                  <RightButtonWrapper>
                    <Button
                      borderType={'square'}
                      color={'filledGreyLight'}
                      size={'md'}
                      icon={{ fileName: 'MoreHorizontal', fill: '#505363', size: 'Large', position: 'right' }}
                      onClick={() => {
                        setIsMoreButtonsModalOpen(true);
                      }}
                    />
                  </RightButtonWrapper>
                </BottomButtonGroup>
              </>
            )}
            {showDeleteButton && (
              <Button
                borderType={'square'}
                color={'filledSecondaryLight'}
                size={'md'}
                text={{ content: '삭제하기', size: 'md' }}
                onClick={handleDeleteOwnTractor}
              />
            )}
          </ButtonGroup>
        )}
      </Wrapper>

      <SlideModal
        isSlideModalOpen={isMoreButtonsModalOpen}
        maxHeight={'312px'}
        showBottomCloseButton
        showXCloseButton={false}
        showSlideButton={false}
        btnCloseColor={'nakedGreyStrong'}
        closeAll={() => {
          ampli.pvmClickMyOwnListMoreModalClose();
          setIsMoreButtonsModalOpen(false);
        }}
      >
        <MoreButtons
          selectedTractor={userOwnProduct}
          onPokeStatusUpdated={handlePokeActionFromMore}
          handleDeleteOwnTractor={() => {
            ampli.pvmClickMyDeleteOwnTractor();
            setIsConfirmDeleteModalOpen(true);
          }}
        />
      </SlideModal>

      <SlideModal
        isSlideModalOpen={isPokeRequestCheckModalOpen}
        maxHeight={'365px'}
        showBottomCloseButton
        showXCloseButton={false}
        showSlideButton={false}
        btnCloseColor={'nakedGreyStrong'}
        closeAll={() => {
          ampli.pvmClickMyOwnListRequestModalClose();
          setIsPokeRequestCheckModalOpen(false);
          refetchList();
        }}
      >
        {isPokeRequestCheckModalOpen && (
          <PokeRequestCheckModalBody
            ownTractorId={userOwnProduct.id}
            handleSellTractorClick={() => {
              ampli.pvmClickMySellOwnTractor({
                page_type: 'PUBLIC',
                position: 'modal',
              });
              handleSellTractorClick();
            }}
            handleDisablePoke={() => {
              const newPokeStatus = !userOwnProduct.isPokeEnabled;
              ampli.pvmClickMyTogglePokeStatus({
                status: newPokeStatus ? 'PUBLIC' : 'PRIVATE',
                position: 'request-check-modal',
              });
              handleTogglePokeStatus();
            }}
          />
        )}
      </SlideModal>

      <ConfirmDeleteOwnTractorModal
        isModalOpen={isConfirmDeleteModalOpen}
        handleModalClose={() => setIsConfirmDeleteModalOpen(false)}
        selectedTractor={userOwnProduct}
        refetchList={refetchList}
      />

      <PopupConfirmModal
        isShow={isDeleteSuccessModalOpen}
        closeModal={() => {
          handleCloseDeleteSuccessModal();
        }}
        title={'🗑 삭제가 완료 되었어요!'}
        description={'브랜드, 모델, 마력을 정확하게\n확인하여 다시 신청해주세요!'}
        buttonArray={[
          {
            text: '확인',
            onClick: () => {
              handleCloseDeleteSuccessModal();
            },
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const BottomButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  flex: 1 0 0;
`;

const LeftButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex-grow: 1;
`;

const RightButtonWrapper = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
`;
